import React from 'react';

interface ViewHeaderProps {
    /** Título de la vista */
    title: string;
    /** Etiqueta pequeña sobre el título (ej. sección) */
    eyebrow?: string;
    /** Conteo de resultados — se muestra en mono junto al título */
    count?: number;
    /** Descripción corta debajo del título */
    description?: string;
    /** Acciones a la derecha (primaria + secundarias) */
    actions?: React.ReactNode;
}

/**
 * ViewHeader — Sistema 2.0.
 * Encabezado estándar de vista: título compacto (NO gigante), conteo en mono,
 * descripción opcional y acciones a la derecha. Sin íconos decorativos.
 * El peso visual lo lleva el contenido, no el encabezado.
 */
export const ViewHeader: React.FC<ViewHeaderProps> = ({ title, eyebrow, count, description, actions }) => (
    /* Una sola fila que se parte SOLA cuando hace falta, en vez de `flex-col`
       hasta `md`.
     *
     * Con `flex-col` toda acción caía a su propio renglón en el teléfono, y en
     * las siete pantallas cuyo `actions` es solo el botón de ayuda eso dejaba
     * un icono suelto en medio de una franja vacía. Se veía como un error de
     * maquetación, porque lo era.
     *
     * Ahora el bloque del título pide 13rem y las acciones ocupan lo suyo: un
     * icono cabe al lado y se queda en la misma línea; un buscador no cabe y
     * baja solo, ya a todo el ancho por el `grow`. El punto de corte lo decide
     * el contenido, no un `md:` escrito a mano.
     *
     * `grow shrink basis-52` y no `flex-1 basis-52`: las dos escriben
     * `flex-basis` y cuál gana lo decide el orden de la hoja de estilos, no el
     * del atributo. Separadas no hay pelea. */
    <div className="flex flex-wrap items-end justify-between gap-3 md:gap-6">
        <div className="min-w-0 grow shrink basis-52">
            {eyebrow && <p className="label-ui mb-1.5">{eyebrow}</p>}
            <div className="flex items-baseline gap-2.5">
                <h1 className="text-[20px] sm:text-[22px] leading-tight font-semibold text-navy-950 tracking-tight">
                    {title}
                </h1>
                {typeof count === 'number' && (
                    <span className="font-data text-[13px] font-medium text-acero-500 tabular-nums">{count}</span>
                )}
            </div>
            {description && <p className="text-sm text-acero-500 mt-1">{description}</p>}
        </div>

        {actions && (
            /* `grow` solo cuenta cuando bajan solas a su renglón: ahí llenan el
               ancho, que es lo que un buscador necesita. Compartiendo línea con
               el título, `justify-end` las mantiene pegadas a la derecha. */
            <div className="flex items-center gap-2 shrink-0 grow justify-end md:grow-0">{actions}</div>
        )}
    </div>
);
